import { useCallback, useState } from 'react';
import { Button, Flex, TextInput } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { isValidContact } from '../EidEasySigningModal.utils';
import { StepBody } from './StepBody';
import { StepFooter } from './StepFooter';
import { StepHeader } from './StepHeader';
import { StepHeading } from './typography';

// Freja eID identifies the signer by their Freja-account email. It's pre-filled
// from the signer's profile but stays editable in case their Freja email differs
// from the stored contact. The country (required by the SDK) is hardcoded to SE
// in the flow — Freja eID is a Swedish service — so it isn't collected here.
interface Props {
	onConfirm: (email: string) => void;
	onGoBack: () => void;
	defaultEmail?: string;
	hideStepHeader?: boolean;
}

export const FrejaInputStep = ({
	onConfirm,
	onGoBack,
	defaultEmail = '',
	hideStepHeader,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [email, setEmail] = useState(defaultEmail);

	const canSubmit = isValidContact(email, SignerContactType.EMAIL);

	const handleConfirm = useCallback(() => {
		if (canSubmit) {
			onConfirm(email);
		}
	}, [canSubmit, email, onConfirm]);

	return (
		<Flex direction="column" w="100%" h="100%">
			{!hideStepHeader && <StepHeader onGoBack={onGoBack} />}
			<StepBody>
				<Flex direction="column" gap={4}>
					<StepHeading>{tCommonUi('eid_easy_signing.freja_title')}</StepHeading>
				</Flex>
				<TextInput
					label={tCommonUi('eid_easy_signing.otp_email_label')}
					value={email}
					type="email"
					autoFocus
					onChange={(event) => setEmail(event.currentTarget.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							handleConfirm();
						}
					}}
					data-testid={TEST_ELEMENT_IDS.EID_EASY_FREJA_EMAIL_INPUT}
				/>
			</StepBody>
			<StepFooter>
				<Button
					variant="filled"
					fullWidth
					disabled={!canSubmit}
					onClick={handleConfirm}
					data-testid={TEST_ELEMENT_IDS.EID_EASY_FREJA_SIGN_BUTTON}
				>
					{tCommonUi('eid_easy_signing.sign')}
				</Button>
			</StepFooter>
		</Flex>
	);
};
